/**
 * Pure word boundary parsing utilities.
 * Separated from SentTrack for testability.
 */

/**
 * Result of parsing word boundaries from a message
 */
export interface WordBoundaryResult {
  /** Array of individual words */
  words: string[];
  /** Array of word lengths corresponding to each word */
  wordLengths: number[];
  /** Total number of words */
  wordCount: number;
  /** Length of the longest word */
  maxWordLength: number;
}

/**
 * Parse a message into word boundaries.
 *
 * @param message - The message to parse (e.g., "HELLO WORLD")
 * @returns WordBoundaryResult with parsed word information
 *
 * @example
 * parseWordBoundaries("HELLO WORLD")
 * // Returns: { words: ["HELLO", "WORLD"], wordLengths: [5, 5], wordCount: 2, maxWordLength: 5 }
 *
 * @example
 * parseWordBoundaries("I AM HERE")
 * // Returns: { words: ["I", "AM", "HERE"], wordLengths: [1, 2, 4], wordCount: 3, maxWordLength: 4 }
 */
export function parseWordBoundaries(message: string): WordBoundaryResult {
  const trimmed = message.trim();

  // Handle empty string case
  if (!trimmed) {
    return {
      words: [],
      wordLengths: [],
      wordCount: 0,
      maxWordLength: 0,
    };
  }

  const words = trimmed.split(/\s+/).filter(Boolean);
  const wordLengths = words.map(w => w.length);
  const wordCount = words.length;
  const maxWordLength = Math.max(...wordLengths);

  return {
    words,
    wordLengths,
    wordCount,
    maxWordLength,
  };
}

/**
 * Check if a decoded message matches the intended word structure.
 * Useful for validation during gameplay.
 */
export function validateWordStructure(
  decodedWords: string[],
  intendedWordLengths: number[]
): boolean {
  if (decodedWords.length !== intendedWordLengths.length) {
    return false;
  }

  return decodedWords.every((word, index) => word.length === intendedWordLengths[index]);
}
